/** Spectral analysis of profiles: FFT amplitude spectrum and dominant period (pitch). */

/** In-place iterative radix-2 complex FFT. Length must be a power of two. */
export function fft(re: Float64Array, im: Float64Array): void {
  const n = re.length;
  if (n & (n - 1)) throw new Error('FFT length must be a power of two');
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (-2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const a = i + k;
        const b = a + len / 2;
        const tr = re[b] * cr - im[b] * ci;
        const ti = re[b] * ci + im[b] * cr;
        re[b] = re[a] - tr;
        im[b] = im[a] - ti;
        re[a] += tr;
        im[a] += ti;
        const nr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = nr;
      }
    }
  }
}

export type WindowFn = 'none' | 'hann' | 'hamming' | 'blackman';
export type Detrend = 'none' | 'mean' | 'linear';

export interface SpectrumSettings {
  window: WindowFn;
  detrend: Detrend;
  /** Zero-padding factor (1, 2, 4, 8) for a finer frequency grid. */
  zeroPad: number;
}

export const DEFAULT_SPECTRUM: SpectrumSettings = { window: 'hann', detrend: 'linear', zeroPad: 4 };

export interface Spectrum {
  /** Spatial frequency in cycles per distance unit. */
  freq: Float64Array;
  /** Single-sided amplitude (a sinusoid of amplitude A reads A at its frequency). */
  amp: Float64Array;
  dominant: { freq: number; period: number; amp: number } | null;
  /** Number of samples actually analysed (longest gap-free run). */
  used: number;
  dx: number;
}

export function windowValue(w: WindowFn, i: number, n: number): number {
  if (n <= 1) return 1;
  const t = (2 * Math.PI * i) / (n - 1);
  switch (w) {
    case 'none':
      return 1;
    case 'hann':
      return 0.5 - 0.5 * Math.cos(t);
    case 'hamming':
      return 0.54 - 0.46 * Math.cos(t);
    case 'blackman':
      return 0.42 - 0.5 * Math.cos(t) + 0.08 * Math.cos(2 * t);
  }
}

/** Longest run of consecutive finite values. */
export function longestRun(y: ArrayLike<number>): [number, number] {
  let best: [number, number] = [0, 0];
  let start = -1;
  for (let i = 0; i <= y.length; i++) {
    const ok = i < y.length && Number.isFinite(y[i]);
    if (ok && start < 0) start = i;
    if (!ok && start >= 0) {
      if (i - start > best[1] - best[0]) best = [start, i];
      start = -1;
    }
  }
  return best;
}

/**
 * Amplitude spectrum of a uniformly sampled profile with sample spacing dx.
 * The dominant frequency is refined by parabolic interpolation of the peak bin.
 */
export function spectrum(y: ArrayLike<number>, dx: number, s: SpectrumSettings): Spectrum {
  const [a, b] = longestRun(y);
  const m = b - a;
  const empty: Spectrum = { freq: new Float64Array(0), amp: new Float64Array(0), dominant: null, used: m, dx };
  if (m < 8 || !(dx > 0)) return empty;
  const seg = new Float64Array(m);
  for (let i = 0; i < m; i++) seg[i] = y[a + i];
  if (s.detrend === 'mean') {
    const mu = seg.reduce((p, q) => p + q, 0) / m;
    for (let i = 0; i < m; i++) seg[i] -= mu;
  } else if (s.detrend === 'linear') {
    const xm = (m - 1) / 2;
    const ym = seg.reduce((p, q) => p + q, 0) / m;
    let num = 0;
    let den = 0;
    for (let i = 0; i < m; i++) {
      num += (i - xm) * (seg[i] - ym);
      den += (i - xm) ** 2;
    }
    const k = den ? num / den : 0;
    for (let i = 0; i < m; i++) seg[i] -= ym + k * (i - xm);
  }
  let wsum = 0;
  for (let i = 0; i < m; i++) {
    const w = windowValue(s.window, i, m);
    seg[i] *= w;
    wsum += w;
  }
  let n = 1;
  while (n < m * Math.max(1, s.zeroPad)) n <<= 1;
  const re = new Float64Array(n);
  const im = new Float64Array(n);
  re.set(seg);
  fft(re, im);
  const half = n / 2 + 1;
  const freq = new Float64Array(half);
  const amp = new Float64Array(half);
  for (let k = 0; k < half; k++) {
    freq[k] = k / (n * dx);
    const mag = Math.hypot(re[k], im[k]) / wsum;
    amp[k] = k === 0 || k === n / 2 ? mag : 2 * mag;
  }
  // Ignore the region dominated by the DC/trend leakage: below ~1.5 cycles over the segment.
  const kmin = Math.max(1, Math.ceil((1.5 * n) / m));
  let kb = -1;
  for (let k = kmin; k < half - 1; k++) {
    if (amp[k] >= amp[k - 1] && amp[k] >= amp[k + 1] && (kb < 0 || amp[k] > amp[kb])) kb = k;
  }
  let dominant: Spectrum['dominant'] = null;
  if (kb > 0) {
    const y0 = amp[kb - 1];
    const y1 = amp[kb];
    const y2 = amp[kb + 1];
    const den = y0 - 2 * y1 + y2;
    const d = den !== 0 ? (0.5 * (y0 - y2)) / den : 0;
    const f = (kb + d) / (n * dx);
    dominant = { freq: f, period: 1 / f, amp: y1 - 0.25 * (y0 - y2) * d };
  }
  return { freq, amp, dominant, used: m, dx };
}
